import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { formatDateTime } from '../utils/timezone';

function memberLabel(m) {
  if (!m) return '—';
  const name = [m.first_name, m.last_name].filter(Boolean).join(' ');
  return `${name || '—'} (${m.wellis_id || '—'})`;
}

export default function SalesTeam() {
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const isZh = i18n.language === 'zh';
  const tt = (en, zh) => (isZh ? zh : en);

  const isAdmin = user && (user.role === 'admin' || user.role === 'superadmin');

  const [members, setMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [expandedUserId, setExpandedUserId] = useState(null);
  const [transactionsByUser, setTransactionsByUser] = useState({});
  const [loadingTransactionsFor, setLoadingTransactionsFor] = useState(null);

  // Send RnR form
  const [sendUserId, setSendUserId] = useState('');
  const [sendAmount, setSendAmount] = useState('');
  const [sendNotes, setSendNotes] = useState('');
  const [sending, setSending] = useState(false);
  const [sendMessage, setSendMessage] = useState('');
  const [sendError, setSendError] = useState('');

  // Promote form
  const [promoteQuery, setPromoteQuery] = useState('');
  const [promoteResults, setPromoteResults] = useState([]);
  const [promoteUserId, setPromoteUserId] = useState('');
  const [promoteSelectedLabel, setPromoteSelectedLabel] = useState('');
  const [promoting, setPromoting] = useState(false);
  const [promoteMessage, setPromoteMessage] = useState('');
  const [promoteError, setPromoteError] = useState('');

  // RnR conversion rate
  const [conversionRate, setConversionRate] = useState(null);
  const [rateInput, setRateInput] = useState('');
  const [savingRate, setSavingRate] = useState(false);
  const [rateMessage, setRateMessage] = useState('');
  const [rateError, setRateError] = useState('');

  // Aging report
  const [agingOrders, setAgingOrders] = useState([]);
  const [loadingAging, setLoadingAging] = useState(false);

  // Pending remittances review
  const [pendingRemittances, setPendingRemittances] = useState([]);
  const [loadingRemittances, setLoadingRemittances] = useState(false);
  const [reviewingId, setReviewingId] = useState(null);
  const [remittanceMessage, setRemittanceMessage] = useState('');
  const [remittanceError, setRemittanceError] = useState('');

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    if (!isAdmin) {
      navigate('/dashboard');
    }
  }, [authLoading, user, navigate, isAdmin]);

  const fetchMembers = useCallback(async () => {
    setLoadingMembers(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/sales-team/list`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        setMembers(data.members || []);
      }
    } catch (err) {
      console.error('[SalesTeam] fetchMembers error:', err);
    } finally {
      setLoadingMembers(false);
    }
  }, [token]);

  useEffect(() => {
    if (!isAdmin) return;
    fetchMembers();
  }, [isAdmin, fetchMembers]);

  const fetchRate = useCallback(async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/user/rnr-conversion-rate`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setConversionRate(data.rate);
        setRateInput(String(data.rate));
      }
    } catch (err) {
      console.error('[SalesTeam] fetchRate error:', err);
    }
  }, [token]);

  useEffect(() => {
    if (!isAdmin) return;
    fetchRate();
  }, [isAdmin, fetchRate]);

  const handleSaveRate = async (e) => {
    e.preventDefault();
    setRateMessage('');
    setRateError('');
    const parsed = parseFloat(rateInput);
    if (isNaN(parsed) || parsed <= 0) {
      setRateError(tt('Rate must be a positive number', '汇率必须为正数'));
      return;
    }
    setSavingRate(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/rnr-conversion-rate/update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ new_rate: parsed }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || tt('Failed to update rate', '更新汇率失败'));
      }
      setConversionRate(data.new_rate);
      setRateMessage(tt('Conversion rate updated', '汇率已更新'));
    } catch (err) {
      setRateError(err.message || tt('Failed to update rate', '更新汇率失败'));
    } finally {
      setSavingRate(false);
    }
  };

  const fetchAgingReport = useCallback(async () => {
    setLoadingAging(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/sales-team-aging-report`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        setAgingOrders(data.orders || []);
      }
    } catch (err) {
      console.error('[SalesTeam] fetchAgingReport error:', err);
    } finally {
      setLoadingAging(false);
    }
  }, [token]);

  const fetchPendingRemittances = useCallback(async () => {
    setLoadingRemittances(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/sales-team-pending-remittances`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        setPendingRemittances(data.remittances || []);
      }
    } catch (err) {
      console.error('[SalesTeam] fetchPendingRemittances error:', err);
    } finally {
      setLoadingRemittances(false);
    }
  }, [token]);

  useEffect(() => {
    if (!isAdmin) return;
    fetchAgingReport();
    fetchPendingRemittances();
  }, [isAdmin, fetchAgingReport, fetchPendingRemittances]);

  const handleReviewRemittance = async (remittanceId, action) => {
    setRemittanceMessage('');
    setRemittanceError('');
    setReviewingId(remittanceId);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/review-remittance`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ remittance_id: remittanceId, action }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || tt('Failed to review remittance', '审核汇款失败'));
      }
      setRemittanceMessage(data.message);
      await Promise.all([fetchPendingRemittances(), fetchAgingReport()]);
    } catch (err) {
      setRemittanceError(err.message || tt('Failed to review remittance', '审核汇款失败'));
    } finally {
      setReviewingId(null);
    }
  };

  const agingBucket = (days) => {
    if (days <= 0) return 'today';
    if (days >= 1 && days <= 5) return '1-5';
    if (days >= 6 && days <= 10) return '6-10';
    return '10+';
  };
  const agingToday = agingOrders.filter((o) => agingBucket(o.days_outstanding) === 'today');
  const aging1to5 = agingOrders.filter((o) => agingBucket(o.days_outstanding) === '1-5');
  const aging6to10 = agingOrders.filter((o) => agingBucket(o.days_outstanding) === '6-10');
  const aging10plus = agingOrders.filter((o) => agingBucket(o.days_outstanding) === '10+');

  // Search approved members for the promote picker
  useEffect(() => {
    if (!isAdmin || !promoteQuery.trim()) {
      setPromoteResults([]);
      return;
    }
    const timeout = setTimeout(async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/users/approved`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          const q = promoteQuery.toLowerCase();
          setPromoteResults(
            (data.users || []).filter((u) => u.wellis_id?.toLowerCase().includes(q)).slice(0, 20)
          );
        }
      } catch (err) {
        console.error('[SalesTeam] promote search error:', err);
      }
    }, 250);
    return () => clearTimeout(timeout);
  }, [promoteQuery, isAdmin, token]);

  const toggleExpand = async (userId) => {
    if (expandedUserId === userId) {
      setExpandedUserId(null);
      return;
    }
    setExpandedUserId(userId);
    if (!transactionsByUser[userId]) {
      setLoadingTransactionsFor(userId);
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/sales-team/transactions?user_id=${userId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (res.ok) {
          setTransactionsByUser((prev) => ({ ...prev, [userId]: data.transactions || [] }));
        }
      } catch (err) {
        console.error('[SalesTeam] fetch transactions error:', err);
      } finally {
        setLoadingTransactionsFor(null);
      }
    }
  };

  const handleSendRnr = async (e) => {
    e.preventDefault();
    setSendMessage('');
    setSendError('');
    if (!sendUserId || !sendAmount || !sendNotes.trim()) {
      setSendError(tt('Please select a member, amount, and notes', '请选择会员、金额和备注'));
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/send-rnr`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          user_id: sendUserId,
          amount: parseFloat(sendAmount),
          notes: sendNotes.trim(),
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || tt('Failed to send RnR Points', '发送 RnR 积分失败'));
      }
      setSendMessage(tt('RnR Points sent successfully', 'RnR 积分发送成功'));
      setSendUserId('');
      setSendAmount('');
      setSendNotes('');
      await fetchMembers();
      setTransactionsByUser((prev) => {
        const next = { ...prev };
        delete next[sendUserId];
        return next;
      });
    } catch (err) {
      setSendError(err.message || tt('Failed to send RnR Points', '发送 RnR 积分失败'));
    } finally {
      setSending(false);
    }
  };

  const handlePromote = async () => {
    setPromoteMessage('');
    setPromoteError('');
    if (!promoteUserId) {
      setPromoteError(tt('Please select a member to promote', '请选择要提升的会员'));
      return;
    }
    if (!window.confirm(tt(
      'Promote this member to Sales Team? They will receive 30,000 RnR Points.',
      '将此会员提升为销售团队？他们将获得 30,000 RnR 积分。'
    ))) {
      return;
    }
    setPromoting(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/promote-sales-team`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ user_id: promoteUserId }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || tt('Failed to promote member', '提升会员失败'));
      }
      setPromoteMessage(data.message || tt('Member promoted to Sales Team', '会员已提升为销售团队'));
      setPromoteUserId('');
      setPromoteSelectedLabel('');
      setPromoteQuery('');
      await fetchMembers();
    } catch (err) {
      setPromoteError(err.message || tt('Failed to promote member', '提升会员失败'));
    } finally {
      setPromoting(false);
    }
  };

  if (!user || !isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen p-margin-mobile pt-24 pb-32" style={{ backgroundColor: '#1a3a2a' }}>
      <div className="max-w-container-max mx-auto">
        <div className="mb-8 border-b pb-6" style={{ borderColor: '#c9a961' }}>
          <h1 className="text-headline-lg font-bold mb-2" style={{ color: '#c9a961' }}>
            {tt('Sales Team', '销售团队')}
          </h1>
          <p className="text-body-md" style={{ color: '#b8a68f' }}>
            {tt('Manage sales-team members, RnR Points, and spending history.', '管理销售团队成员、RnR 积分和消费历史。')}
          </p>
        </div>

        {/* Aging Report */}
        <div className="border rounded-lg p-6 mb-8" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
          <h2 className="text-headline-sm font-bold mb-2" style={{ color: '#c9a961' }}>
            {tt('Aging Report — Unremitted Signups', '账龄报告 — 未汇款注册')}
          </h2>
          <p className="text-body-xs mb-4" style={{ color: '#b8a68f' }}>
            {tt(
              'Signups funded via Internal Signup where the sales-team member has not yet remitted the full package price back.',
              '通过内部注册资助的注册，销售团队成员尚未将套餐全款汇回。'
            )}
          </p>
          {loadingAging ? (
            <p style={{ color: '#b8a68f' }}>{tt('Loading...', '加载中...')}</p>
          ) : agingOrders.length === 0 ? (
            <p style={{ color: '#b8a68f' }}>{tt('No outstanding signups.', '暂无未结清的注册。')}</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {[
                { label: tt('Sales Funded Today', '今日销售资助'), color: '#b8a68f', rows: agingToday },
                { label: tt('1–5 Days', '1–5 天'), color: '#c9a961', rows: aging1to5 },
                { label: tt('6–10 Days', '6–10 天'), color: '#d97706', rows: aging6to10 },
                { label: tt('Above 10 Days', '10 天以上'), color: '#e05252', rows: aging10plus },
              ].map((bucket) => (
                <div key={bucket.label} className="border rounded-lg p-4" style={{ borderColor: bucket.color, backgroundColor: '#152214' }}>
                  <p className="font-bold mb-3" style={{ color: bucket.color }}>{bucket.label} ({bucket.rows.length})</p>
                  {bucket.rows.length === 0 ? (
                    <p className="text-body-xs" style={{ color: '#999999' }}>{tt('None', '无')}</p>
                  ) : (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {bucket.rows.map((o) => (
                        <div key={o.package_order_id} className="text-body-xs border-b pb-2" style={{ borderColor: 'rgba(201,169,97,0.2)' }}>
                          <p style={{ color: '#c9a961' }}>{memberLabel(o.sales_team_member)}</p>
                          <p style={{ color: '#b8a68f' }}>{tt('New member', '新会员')}: {memberLabel(o.new_member)}</p>
                          <p style={{ color: '#b8a68f' }}>{o.package_code} · {tt('Outstanding', '未结')}: {Number(o.outstanding).toFixed(2)} CP</p>
                          {o.pending_remitted > 0 && (
                            <p style={{ color: '#999999' }}>{tt('Pending review', '待审核')}: {Number(o.pending_remitted).toFixed(2)} CP</p>
                          )}
                          <p style={{ color: '#999999' }}>{o.days_outstanding} {tt('days', '天')}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pending Remittances Review */}
        <div className="border rounded-lg p-6 mb-8" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
          <h2 className="text-headline-sm font-bold mb-4" style={{ color: '#c9a961' }}>
            {tt('Pending Cash Remittances', '待审核汇款')}
          </h2>
          {remittanceMessage && <p className="text-body-sm mb-3" style={{ color: '#c9a961' }}>{remittanceMessage}</p>}
          {remittanceError && <p className="text-body-sm mb-3" style={{ color: '#e05252' }}>{remittanceError}</p>}
          {loadingRemittances ? (
            <p style={{ color: '#b8a68f' }}>{tt('Loading...', '加载中...')}</p>
          ) : pendingRemittances.length === 0 ? (
            <p style={{ color: '#b8a68f' }}>{tt('No pending remittances.', '暂无待审核汇款。')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-body-sm" style={{ color: '#b8a68f' }}>
                <thead>
                  <tr className="border-b" style={{ borderColor: '#3d5a4a' }}>
                    <th className="p-2 text-left">{tt('Sales-Team Member', '销售团队成员')}</th>
                    <th className="p-2 text-left">{tt('New Member', '新会员')}</th>
                    <th className="p-2 text-left">{tt('Package', '套餐')}</th>
                    <th className="p-2 text-right">{tt('Amount', '金额')}</th>
                    <th className="p-2 text-left">{tt('Notes', '备注')}</th>
                    <th className="p-2 text-left">{tt('Submitted', '提交时间')}</th>
                    <th className="p-2 text-left">{tt('Action', '操作')}</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingRemittances.map((r) => (
                    <tr key={r.id} className="border-b" style={{ borderColor: '#152214' }}>
                      <td className="p-2" style={{ color: '#c9a961' }}>{memberLabel(r.sales_team_member)}</td>
                      <td className="p-2">{memberLabel(r.new_member)}</td>
                      <td className="p-2">{r.package_code}</td>
                      <td className="p-2 text-right">{Number(r.amount_cp).toFixed(2)}</td>
                      <td className="p-2">{r.notes || '—'}</td>
                      <td className="p-2">{formatDateTime(r.submitted_at)}</td>
                      <td className="p-2">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleReviewRemittance(r.id, 'confirm')}
                            disabled={reviewingId === r.id}
                            className="px-3 py-1 text-xs rounded font-medium disabled:opacity-50"
                            style={{ backgroundColor: '#c9a961', color: '#0d2818' }}
                          >
                            {tt('Confirm', '确认')}
                          </button>
                          <button
                            onClick={() => handleReviewRemittance(r.id, 'reject')}
                            disabled={reviewingId === r.id}
                            className="px-3 py-1 text-xs rounded font-medium disabled:opacity-50"
                            style={{ backgroundColor: 'transparent', color: '#e05252', border: '1px solid #e05252' }}
                          >
                            {tt('Reject', '拒绝')}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Promote to Sales Team */}
        <div className="border rounded-lg p-6 mb-8" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
          <h2 className="text-headline-sm font-bold mb-4" style={{ color: '#c9a961' }}>
            {tt('Promote to Sales Team', '提升为销售团队')}
          </h2>
          <p className="text-body-xs mb-4" style={{ color: '#b8a68f' }}>
            {tt('Grants a one-time 30,000 RnR Points allocation.', '一次性授予 30,000 RnR 积分。')}
          </p>
          {promoteMessage && (
            <p className="text-body-sm mb-3" style={{ color: '#c9a961' }}>{promoteMessage}</p>
          )}
          {promoteError && (
            <p className="text-body-sm mb-3" style={{ color: '#e05252' }}>{promoteError}</p>
          )}
          <div className="mb-3">
            <label className="block text-body-sm mb-2" style={{ color: '#b8a68f' }}>
              {tt('Search Member by Wellis ID', '按 Wellis ID 搜索会员')}
            </label>
            <input
              type="text"
              value={promoteQuery}
              onChange={(e) => setPromoteQuery(e.target.value)}
              placeholder={tt('Enter Wellis ID to search...', '输入 Wellis ID 进行搜索...')}
              className="w-full p-3 rounded border mb-2"
              style={{ backgroundColor: '#152214', borderColor: '#c9a961', color: '#b8a68f' }}
            />
            {promoteResults.length > 0 && (
              <div className="border rounded" style={{ backgroundColor: '#152214', borderColor: '#3d5a4a', maxHeight: '200px', overflowY: 'auto' }}>
                {promoteResults.map((m) => (
                  <div
                    key={m.id}
                    onClick={() => {
                      setPromoteUserId(m.id);
                      setPromoteSelectedLabel(memberLabel(m));
                      setPromoteQuery('');
                      setPromoteResults([]);
                    }}
                    className="p-3 border-b cursor-pointer hover:opacity-80 transition-opacity"
                    style={{ borderColor: '#3d5a4a', color: '#b8a68f' }}
                  >
                    <div className="font-medium" style={{ color: '#c9a961' }}>{m.wellis_id}</div>
                    <div className="text-body-xs">{m.first_name} {m.last_name} ({m.email})</div>
                  </div>
                ))}
              </div>
            )}
            {promoteSelectedLabel && (
              <div className="p-3 mt-2 rounded border" style={{ backgroundColor: '#152214', borderColor: '#c9a961' }}>
                <p style={{ color: '#c9a961' }}>{promoteSelectedLabel}</p>
              </div>
            )}
          </div>
          <button
            onClick={handlePromote}
            disabled={promoting || !promoteUserId}
            className="px-6 py-3 rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50 text-black"
            style={{ backgroundColor: '#c9a961' }}
          >
            {promoting ? tt('Promoting...', '提升中...') : tt('Promote to Sales Team', '提升为销售团队')}
          </button>
        </div>

        {/* RnR -> Credit Points Conversion Rate */}
        <div className="border rounded-lg p-6 mb-8" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
          <h2 className="text-headline-sm font-bold mb-4" style={{ color: '#c9a961' }}>
            {tt('RnR → Credit Points Conversion Rate', 'RnR → 认购积分转换汇率')}
          </h2>
          <p className="text-body-xs mb-4" style={{ color: '#b8a68f' }}>
            {tt('Current rate: 1 RnR = ', '当前汇率：1 RnR = ')}
            <span className="font-bold" style={{ color: '#c9a961' }}>{conversionRate === null ? '...' : conversionRate}</span>
            {tt(' Credit Points. Applies to manual conversion and to auto-conversion during Internal Signup.', ' 认购积分。适用于手动转换以及内部注册中的自动转换。')}
          </p>
          {rateMessage && <p className="text-body-sm mb-3" style={{ color: '#c9a961' }}>{rateMessage}</p>}
          {rateError && <p className="text-body-sm mb-3" style={{ color: '#e05252' }}>{rateError}</p>}
          <form onSubmit={handleSaveRate} className="flex gap-2 items-end">
            <div className="flex-1">
              <label className="block text-body-sm mb-2" style={{ color: '#b8a68f' }}>
                {tt('New Rate', '新汇率')}
              </label>
              <input
                type="number"
                step="0.0001"
                min="0.0001"
                value={rateInput}
                onChange={(e) => setRateInput(e.target.value)}
                className="w-full p-3 rounded border"
                style={{ backgroundColor: '#152214', borderColor: '#c9a961', color: '#b8a68f' }}
              />
            </div>
            <button
              type="submit"
              disabled={savingRate}
              className="px-6 py-3 rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50 text-black"
              style={{ backgroundColor: '#c9a961' }}
            >
              {savingRate ? tt('Saving...', '保存中...') : tt('Save Rate', '保存汇率')}
            </button>
          </form>
        </div>

        {/* Send RnR Points */}
        <div className="border rounded-lg p-6 mb-8" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
          <h2 className="text-headline-sm font-bold mb-4" style={{ color: '#c9a961' }}>
            {tt('Send RnR Points', '发送 RnR 积分')}
          </h2>
          {sendMessage && <p className="text-body-sm mb-3" style={{ color: '#c9a961' }}>{sendMessage}</p>}
          {sendError && <p className="text-body-sm mb-3" style={{ color: '#e05252' }}>{sendError}</p>}
          <form onSubmit={handleSendRnr} className="space-y-4">
            <div>
              <label className="block text-body-sm mb-2" style={{ color: '#b8a68f' }}>
                {tt('Sales-Team Member', '销售团队成员')}
              </label>
              <select
                value={sendUserId}
                onChange={(e) => setSendUserId(e.target.value)}
                className="w-full p-3 rounded border"
                style={{ backgroundColor: '#152214', borderColor: '#c9a961', color: '#b8a68f' }}
              >
                <option value="">{tt('Select a member...', '选择会员...')}</option>
                {members.map((m) => (
                  <option key={m.id} value={m.id}>{memberLabel(m)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-body-sm mb-2" style={{ color: '#b8a68f' }}>
                {tt('Amount (RnR)', '数量 (RnR)')}
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={sendAmount}
                onChange={(e) => setSendAmount(e.target.value)}
                className="w-full p-3 rounded border"
                style={{ backgroundColor: '#152214', borderColor: '#c9a961', color: '#b8a68f' }}
              />
            </div>
            <div>
              <label className="block text-body-sm mb-2" style={{ color: '#b8a68f' }}>
                {tt('Notes', '备注')} <span style={{ color: '#c9a961' }}>*</span>
              </label>
              <textarea
                value={sendNotes}
                onChange={(e) => setSendNotes(e.target.value)}
                rows="2"
                className="w-full p-3 rounded border"
                style={{ backgroundColor: '#152214', borderColor: '#c9a961', color: '#b8a68f' }}
              />
            </div>
            <button
              type="submit"
              disabled={sending || !sendUserId || !sendAmount || !sendNotes.trim()}
              className="w-full px-4 py-3 rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50 text-black"
              style={{ backgroundColor: '#c9a961' }}
            >
              {sending ? tt('Sending...', '发送中...') : tt('Send RnR Points', '发送 RnR 积分')}
            </button>
          </form>
        </div>

        {/* Sales Team Member List */}
        <div className="border rounded-lg p-6" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
          <h2 className="text-headline-sm font-bold mb-4" style={{ color: '#c9a961' }}>
            {tt('Sales Team Members', '销售团队成员')}
          </h2>
          {loadingMembers ? (
            <p style={{ color: '#b8a68f' }}>{tt('Loading...', '加载中...')}</p>
          ) : members.length === 0 ? (
            <p style={{ color: '#b8a68f' }}>{tt('No sales-team members yet.', '暂无销售团队成员。')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-body-sm" style={{ color: '#b8a68f' }}>
                <thead>
                  <tr className="border-b" style={{ borderColor: '#3d5a4a' }}>
                    <th className="p-2 text-left">{tt('Name', '姓名')}</th>
                    <th className="p-2 text-left">{tt('Wellis ID', 'Wellis ID')}</th>
                    <th className="p-2 text-right">{tt('RnR Balance', 'RnR 余额')}</th>
                    <th className="p-2 text-right">{tt('Credit Balance', '认购积分余额')}</th>
                    <th className="p-2 text-left">{tt('History', '历史')}</th>
                  </tr>
                </thead>
                <tbody>
                  {members.map((m) => (
                    <React.Fragment key={m.id}>
                      <tr className="border-b" style={{ borderColor: '#152214' }}>
                        <td className="p-2">{m.first_name} {m.last_name}</td>
                        <td className="p-2 font-mono" style={{ color: '#c9a961' }}>{m.wellis_id}</td>
                        <td className="p-2 text-right">{Number(m.rnr_point_balance).toFixed(2)}</td>
                        <td className="p-2 text-right">{Number(m.credit_balance).toFixed(2)}</td>
                        <td className="p-2">
                          <button
                            onClick={() => toggleExpand(m.id)}
                            className="px-3 py-1 text-xs rounded font-medium"
                            style={{ backgroundColor: '#3d5a4a', color: '#c9a961' }}
                          >
                            {expandedUserId === m.id ? tt('Hide', '隐藏') : tt('View', '查看')}
                          </button>
                        </td>
                      </tr>
                      {expandedUserId === m.id && (
                        <tr>
                          <td colSpan={5} className="p-3" style={{ backgroundColor: '#152214' }}>
                            {loadingTransactionsFor === m.id ? (
                              <p>{tt('Loading history...', '加载历史中...')}</p>
                            ) : (transactionsByUser[m.id] || []).length === 0 ? (
                              <p>{tt('No transactions yet.', '暂无交易。')}</p>
                            ) : (
                              <div className="space-y-2 max-h-64 overflow-y-auto">
                                {(transactionsByUser[m.id] || []).map((txn) => (
                                  <div key={txn.id} className="flex justify-between items-start p-2 rounded" style={{ backgroundColor: '#0d2818' }}>
                                    <div>
                                      <p className="font-medium" style={{ color: '#c9a961' }}>
                                        {txn.point_type} · {txn.type}
                                      </p>
                                      {txn.notes && <p className="text-body-xs">{txn.notes}</p>}
                                      <p className="text-body-xs" style={{ color: '#999' }}>{formatDateTime(txn.created_at)}</p>
                                    </div>
                                    <div className="text-right">
                                      <p className={txn.amount >= 0 ? 'text-green-400' : 'text-red-400'}>
                                        {txn.amount >= 0 ? '+' : ''}{Number(txn.amount).toFixed(2)}
                                      </p>
                                      <p className="text-body-xs">{tt('Balance', '余额')}: {Number(txn.balance_after).toFixed(2)}</p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
